import * as Sentry from '@sentry/react';
import React, { useEffect, useRef, useState, useCallback } from 'react';
// Build `legacy` : transpilé pour les navigateurs de la browserslist du projet.
// CRA ne transpile pas node_modules, et le build moderne de pdf.js emploie une
// syntaxe que Babel ne verra jamais passer ici.
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs';
import { Loader2, AlertCircle } from 'lucide-react';

// Worker pdf.js bundlé par webpack 5 (CRA) via new URL(..., import.meta.url) :
// pas de CDN, pas de dépendance à un hébergement externe.
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

// Rendu net MAIS borné (net sur retina/au zoom, sans faire ramer ni planter les
// mobiles). Garde-fous :
//  - MAX_CANVAS_DIM : dimension max (largeur ET hauteur) d'un canvas de page. Au
//    delà de ~4096 px, iOS Safari rend des pages BLANCHES / plante l'onglet — on
//    reste donc franchement en dessous.
//  - PIXEL_BUDGET : budget TOTAL de pixels (RGBA) réparti sur toutes les pages —
//    ~16 M px ≈ 64 Mo, sûr même sur mobile (les canvas rendus sont conservés).
const MAX_CANVAS_DIM = 2200;
const PIXEL_BUDGET = 14_000_000;
// Rendu PARESSEUX : on ne peint une page que lorsqu'elle approche de l'écran
// (marge = ~1,5 écran avant/après) → ouverture immédiate (seule la 1re page est
// peinte au chargement) et défilement fluide (les autres se peignent au fil du
// scroll, sans monopoliser le worker au démarrage).
const RENDER_ROOT_MARGIN = '150% 0px';
// Timeout de sécurité : si le PDF ne s'affiche jamais (worker bloqué, réseau
// figé…), on montre la carte d'erreur au lieu d'un spinner infini.
const LOAD_TIMEOUT_MS = 30_000;

/**
 * Visualiseur PDF page à page (lien public du devis) qui TRACE le temps passé
 * PAR PAGE. On réutilise la balise de suivi existante (`temps_page` + sections) :
 * chaque page est une « section » (libellée par le bloc qui y démarre, cf.
 * blockLabels, sinon « Page N ») → la heatmap de l'onglet Suivi affiche
 * automatiquement quelles pages/blocs ont été lus, et combien de temps.
 *
 * Rendu PARESSEUX : dès que la structure du PDF est connue on pose une carte
 * (placeholder) à la bonne taille pour chaque page ; seule la 1re page est
 * peinte tout de suite (ouverture instantanée), les autres sont peintes quand
 * elles approchent de l'écran. Le suivi démarre dès la 1re page.
 *
 * Props :
 *  - pdfUrl : URL du PDF à afficher (devisAPI.publicPdfUrl(token, {inline:true}))
 *             — `inline` pour que l'affichage ne compte pas comme téléchargement.
 *  - trackUrl : endpoint balise (devisAPI.publicTrackUrl(token)) — null en aperçu.
 *  - blockLabels : libellés « quel bloc démarre sur quelle page » (suivi PAR
 *    BLOC), indexés par page (0-based). Le suivi remonte alors le NOM DU BLOC
 *    lu plutôt que « Page N ». Vide → repli « Page N » (rétrocompatible).
 *  - onLoaded : appelé UNE fois quand le PDF est chargé. Sert au parent à
 *    rafraîchir `blockLabels` : sur un devis modifié après envoi, la table
 *    bloc→page est figée par la requête /pdf elle-même (donc disponible une fois
 *    le PDF chargé), alors qu'elle était encore vide au 1er chargement de page.
 */
export default function DevisPdfViewer({ pdfUrl, trackUrl, blockLabels, onLoaded }) {
  const containerRef = useRef(null);
  const pagesRef = useRef([]);          // [{ seconds, visible, pendingViews, canvas, rendered, rendering }]
  const totalsRef = useRef({ total: 0, sent: 0 });
  // Ref (pas une dép d'effet) : les libellés peuvent arriver après le 1er rendu
  // sans qu'on veuille relancer tout le rendu PDF. Le nom du bloc est résolu à
  // l'instant du flush, donc une arrivée tardive est prise en compte.
  const blockLabelsRef = useRef(blockLabels || []);
  blockLabelsRef.current = Array.isArray(blockLabels) ? blockLabels : [];
  // Dédup des vues PAR BLOC : un bloc qui s'étale sur plusieurs pages ne compte
  // qu'UNE vue (le backend agrège les sections par libellé, donc sans ça un bloc
  // de 3 pages serait « vu 3 fois »). En repli « Page N », chaque page a un
  // libellé unique → revient exactement à une vue par page (rétrocompatible).
  const countedLabelsRef = useRef(new Set());
  // Ref (pas une dép d'effet) : le parent peut passer une closure recréée à
  // chaque rendu sans qu'on relance le rendu PDF.
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  const [numPages, setNumPages] = useState(0);
  const [state, setState] = useState('loading');   // loading | ok | error

  // Envoi de la balise : delta de temps + secondes par page + incrément de
  // « vues » (« sections »). IMPORTANT : `views` ne compte une vue que lorsque la
  // page ENTRE réellement dans le champ (transition non-visible → visible), pas à
  // chaque flush périodique — sinon une lecture continue serait « vue N fois ».
  const flush = useCallback(() => {
    if (!trackUrl) return;
    const t = totalsRef.current;
    const delta = Math.max(0, t.total - t.sent);
    const sections = [];
    const labels = blockLabelsRef.current;
    pagesRef.current.forEach((p, i) => {
      if (p && (p.seconds > 0 || p.pendingViews > 0)) {
        // Libellé = nom du bloc qui démarre sur cette page (suivi PAR BLOC) ;
        // repli « Page N » tant que la table bloc→page n'est pas figée.
        const label = (labels[i] || '').trim() || `Page ${i + 1}`;
        sections.push({ label, seconds: p.seconds, views: p.pendingViews });
      }
    });
    if (delta <= 0 && sections.length === 0) return;   // rien de nouveau à envoyer
    const payload = JSON.stringify({ type: 'temps_page', meta: { seconds: delta, sections } });
    // On ne remet les compteurs à zéro QUE si la balise a bien été acceptée par le
    // navigateur. sendBeacon renvoie false si la file est pleine ; comme une vue
    // n'est émise qu'à l'entrée de la page, effacer sans envoi la perdrait pour
    // toujours — on garde alors les deltas (temps + vues) pour le prochain flush.
    let queued = false;
    try {
      if (navigator.sendBeacon) {
        queued = navigator.sendBeacon(trackUrl, new Blob([payload], { type: 'application/json' }));
      } else {
        fetch(trackUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true }).catch(() => {});
        queued = true;   // pas de retour synchrone pour fetch : on considère l'envoi tenté
      }
    } catch (e) { queued = false; /* le suivi ne doit jamais casser l'affichage */ }
    if (!queued) return;
    t.sent = t.total;
    pagesRef.current.forEach((p) => { if (p) { p.seconds = 0; p.pendingViews = 0; } });
  }, [trackUrl]);

  // Structure du PDF + rendu paresseux + suivi, dans le MÊME effet : on pose les
  // cartes de toutes les pages (à la bonne taille) dès que la structure est
  // connue, on peint la 1re tout de suite et les autres à l'approche de l'écran.
  useEffect(() => {
    let cancelled = false;
    let loadingTask;
    let pdfDoc = null;
    let trackIO = null;
    let renderIO = null;
    let tick = null;
    let flusher = null;
    // Filet de sécurité : rien affiché au bout de LOAD_TIMEOUT_MS → carte d'erreur
    // (worker pdf.js bloqué / réseau figé) plutôt qu'un spinner infini.
    let loadTimer = setTimeout(() => {
      if (!cancelled) setState((s) => (s === 'ok' ? s : 'error'));
    }, LOAD_TIMEOUT_MS);

    const startTracking = () => {
      if (tick || !trackUrl) return;   // rien à faire en aperçu (trackUrl null)
      tick = setInterval(() => {
        if (document.hidden) return;
        // On ne compte le temps que sur une page VISIBLE **et déjà peinte** — pas
        // sur un placeholder blanc (rendu paresseux pas encore terminé). Le temps
        // TOTAL n'avance que si au moins une telle page est active, sinon le
        // « Temps total » se décorrèlerait du temps par section (placeholder blanc).
        let anyActive = false;
        pagesRef.current.forEach((p) => { if (p && p.visible && p.rendered) { p.seconds += 1; anyActive = true; } });
        if (anyActive) totalsRef.current.total += 1;
      }, 1000);
      flusher = setInterval(flush, 8000);
    };

    // Compte UNE vue pour le bloc de la page idx, si elle est visible ET peinte.
    // Dédup par libellé (un bloc multi-pages = une vue). Appelée à l'entrée dans
    // le champ ET à la fin du rendu (une page peinte alors qu'elle est déjà à
    // l'écran doit être comptée sans attendre un nouveau scroll).
    const maybeCountView = (idx) => {
      const p = pagesRef.current[idx];
      if (!p || !p.visible || !p.rendered) return;
      const labels = blockLabelsRef.current;
      const label = (labels[idx] || '').trim() || `Page ${idx + 1}`;
      if (!countedLabelsRef.current.has(label)) {
        p.pendingViews += 1;
        countedLabelsRef.current.add(label);
      }
    };

    const onHide = () => { if (document.hidden) flush(); };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', flush);

    // Peint une page dans son canvas (une seule fois). Renvoie true si peinte.
    // Appelée pour la 1re page au chargement, puis par l'observer de rendu à
    // l'approche de l'écran. `critical` (1re page) : on RELAIE l'échec pour que
    // l'appelant bascule sur la carte d'erreur au lieu d'afficher une page vide.
    const renderPage = async (idx, { critical = false } = {}) => {
      const rec = pagesRef.current[idx];
      if (!rec || !pdfDoc) return false;
      if (rec.rendered) return true;
      if (rec.rendering) return rec.rendered;
      rec.rendering = true;
      try {
        const page = await pdfDoc.getPage(idx + 1);
        if (cancelled) return false;
        const base = page.getViewport({ scale: 1 });
        const container = containerRef.current;
        const width = Math.min(container ? container.clientWidth : 800, 900);
        const cssScale = Math.max(0.2, width / base.width);
        // Qualité : équilibre net/perf. Les images du PDF sont recompressées à
        // ~150 DPI côté serveur, donc rendre le canvas bien au-delà n'ajoute plus
        // de finesse (juste du coût). ~1.5× l'affichage suffit à rester net sur
        // retina et au zoom, sans le sur-coût qui faisait ramer.
        const dpr = window.devicePixelRatio || 1;
        const quality = Math.min(Math.max(dpr, 1.35), 1.6);
        let renderScale = cssScale * quality;
        // Plafond de DIMENSION (largeur ET hauteur) : sous la limite ~4096 px
        // d'iOS Safari (au-delà → pages blanches / crash mobile).
        const maxByDim = MAX_CANVAS_DIM / Math.max(base.width, base.height);
        renderScale = Math.min(renderScale, maxByDim);
        // Budget mémoire au niveau du DOCUMENT (les canvas rendus sont conservés).
        // On ne descend jamais sous l'échelle d'affichage.
        const perPageBudgetPx = PIXEL_BUDGET / Math.max(1, pagesRef.current.length);
        const maxScaleByBudget = Math.sqrt(perPageBudgetPx / (base.width * base.height));
        renderScale = Math.max(cssScale, Math.min(renderScale, maxScaleByBudget));
        const viewport = page.getViewport({ scale: renderScale });
        const canvas = rec.canvas;
        if (!canvas) return false;
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
        if (cancelled) return false;
        rec.rendered = true;
        // Page peinte alors qu'elle est déjà à l'écran → compter sa vue maintenant
        // (sinon rien ne la compterait jusqu'au prochain scroll).
        maybeCountView(idx);
        return true;
      } catch (e) {
        // Un échec de rendu d'UNE page ne doit pas casser tout le visualiseur…
        if (!cancelled) Sentry.captureException(e);
        // …mais l'échec de la 1re page DOIT remonter (sinon page blanche « ok »).
        if (critical) throw e;
        return false;
      } finally {
        rec.rendering = false;
      }
    };

    (async () => {
      try {
        // isEvalSupported:false — défense en profondeur : désactive l'exécution
        // JS embarquée du moteur (surface de CVE-2024-4367). On n'affiche que nos
        // propres PDF de devis (générés côté backend), jamais un PDF tiers.
        // Chargement par Range (HTTP 206) : on ne télécharge que les octets
        // nécessaires à l'affichage. Nos PDF récents sont légers (~250 Ko),
        // mais les anciens devis figés (avant l'allègement des images)
        // peuvent peser plusieurs Mo ; un téléchargement complet en streaming
        // risquerait alors de dépasser le timeout de 30 s du visualiseur.
        // Le mode Range protège ces PDF hérités sans pénaliser les PDF légers.
        loadingTask = pdfjsLib.getDocument({
          url: pdfUrl,
          isEvalSupported: false,
          disableAutoFetch: true,
          disableStream: true,
          rangeChunkSize: 262144,
        });
        const pdf = await loadingTask.promise;
        if (cancelled) return;
        pdfDoc = pdf;
        setNumPages(pdf.numPages);
        pagesRef.current = Array.from({ length: pdf.numPages }, () => ({ seconds: 0, visible: false, pendingViews: 0, canvas: null, rendered: false, rendering: false }));
        countedLabelsRef.current = new Set();
        const container = containerRef.current;
        if (!container) return;

        // Suivi de visibilité (temps + vues) : seuil réel de présence à l'écran.
        if (trackUrl && 'IntersectionObserver' in window) {
          trackIO = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
              const idx = parseInt(e.target.dataset.page, 10) - 1;
              const p = pagesRef.current[idx];
              if (!p) return;
              const nowVisible = e.isIntersecting && e.intersectionRatio > 0.3;
              p.visible = nowVisible;
              // Une vue = UNE seule fois par BLOC et par session (dédup par
              // libellé dans maybeCountView) ; comptée seulement si la page est
              // réellement peinte (pas un placeholder blanc).
              if (nowVisible) maybeCountView(idx);
            });
          }, { threshold: [0, 0.3, 0.6] });
        }

        // Rendu paresseux : peint une page dès qu'elle approche de l'écran.
        if ('IntersectionObserver' in window) {
          renderIO = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
              if (!e.isIntersecting) return;
              const idx = parseInt(e.target.dataset.page, 10) - 1;
              renderPage(idx);
            });
          }, { root: null, rootMargin: RENDER_ROOT_MARGIN, threshold: 0 });
        }

        // On pose TOUTES les cartes tout de suite, à la bonne taille (ratio réel
        // de chaque page) : le défilement est correct et le suivi opère même
        // avant que la page soit peinte. getPage ne fait que lire les métadonnées
        // (pas de rendu) — c'est rapide, même sur un long devis.
        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelled) return;
          const page = await pdf.getPage(i);
          if (cancelled) return;
          const base = page.getViewport({ scale: 1 });
          const wrap = document.createElement('div');
          wrap.dataset.page = String(i);
          // Carte au RATIO exact de la page : réserve la place avant le rendu ET
          // reste correcte si la largeur change (rotation d'écran) — la hauteur
          // suit alors la largeur via aspect-ratio, sans étirer la page.
          wrap.style.cssText = `position:relative;width:100%;aspect-ratio:${base.width}/${base.height};margin:0 auto 12px;box-shadow:0 1px 6px rgba(0,0,0,.15);background:#fff;border-radius:4px;overflow:hidden`;
          const canvas = document.createElement('canvas');
          canvas.style.cssText = 'display:block;width:100%;height:100%';
          wrap.appendChild(canvas);
          container.appendChild(wrap);
          const rec = pagesRef.current[i - 1];
          if (rec) rec.canvas = canvas;

          if (i === 1) {
            // 1re page peinte tout de suite → ouverture instantanée. Son échec
            // (critical) remonte à l'outer catch → carte d'erreur, pas page vide.
            await renderPage(0, { critical: true });
            if (cancelled) return;
            clearTimeout(loadTimer); setState('ok'); startTracking();
            // Le PDF est chargé → la requête /pdf a figé la table bloc→page :
            // le parent peut la rafraîchir (cas du devis modifié après envoi).
            try { onLoadedRef.current && onLoadedRef.current(); } catch (e) { /* jamais casser l'affichage */ }
          }
          if (trackIO) trackIO.observe(wrap);
          if (renderIO) renderIO.observe(wrap);
        }
      } catch (e) {
        if (cancelled) return;
        Sentry.captureException(e);
        setState('error');
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(loadTimer);
      try { loadingTask && loadingTask.destroy(); } catch (e) { /* noop */ }
      if (trackIO) trackIO.disconnect();
      if (renderIO) renderIO.disconnect();
      if (tick) clearInterval(tick);
      if (flusher) clearInterval(flusher);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, [pdfUrl, trackUrl, flush]);

  return (
    <div style={{ background: '#f1f0ee', minHeight: '60vh', padding: '16px 12px' }}>
      {state === 'loading' && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#02a96d' }} />
        </div>
      )}
      {state === 'error' && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: 40, color: '#48544D', textAlign: 'center' }}>
          <AlertCircle className="w-7 h-7" style={{ color: '#d1493f' }} />
          <p style={{ fontSize: 13 }}>Impossible d'afficher le PDF. Utilisez le bouton « Télécharger le PDF ».</p>
        </div>
      )}
      <div ref={containerRef} style={{ maxWidth: 900, margin: '0 auto' }} />
      {state === 'ok' && numPages > 0 && (
        <p style={{ textAlign: 'center', fontSize: 11, color: '#8a938d', marginTop: 8 }}>{numPages} page(s)</p>
      )}
    </div>
  );
}
